/**
 * Turns the measured marketplace metrics into a short list of things an
 * operator should look at. Pure: same input, same list. Every item is derived
 * from a number the API returned; nothing is invented, and an empty
 * marketplace produces an empty list.
 */

import type { MarketplaceDto } from "@/modules/dashboard/insights/types";

export type AttentionTone = "danger" | "warning" | "info";

export interface AttentionSignal {
  signal_type: "supply_gap" | "demand_gap" | "utilization" | "reliability" | "conversion";
  target_type: "bookable_resource" | "business" | "sport";
  target_id: string;
}

export interface AttentionItem {
  id: string;
  tone: AttentionTone;
  title: string;
  detail: string;
  href?: string;
  /** When present, the operator can log this as a Marketplace Health Signal. */
  signal?: AttentionSignal;
}

/** A rate needs this many bookings behind it before it is called a problem. */
export const MIN_BOOKINGS_FOR_RATE = 5;
export const HIGH_CANCELLATION_PERCENT = 30;
export const LOW_CHECKOUT_CONVERSION_PERCENT = 50;
const MAX_PER_RULE = 8;
const MAX_SPORT_GAP_ROWS = 3;

export function buildAttention(m: MarketplaceDto): AttentionItem[] {
  const items: AttentionItem[] = [];
  const s = m.supply;

  for (const r of m.utilisation.resources.filter((x) => !x.has_hours).slice(0, MAX_PER_RULE)) {
    items.push({
      id: `no-hours-${r.resource_id}`,
      tone: "warning",
      title: `${r.resource} has no opening hours`,
      detail: `${r.business} has not set when this court is open, so players cannot book it.`,
      href: `/app/businesses/resources/${r.resource_id}`,
      signal: { signal_type: "supply_gap", target_type: "bookable_resource", target_id: r.resource_id },
    });
  }

  if (s.active_offers_without_price > 0) {
    items.push({
      id: "offers-no-price",
      tone: "warning",
      title: `${s.active_offers_without_price} active listing${s.active_offers_without_price === 1 ? "" : "s"} without a price`,
      detail: "A listing with no price cannot be sold.",
    });
  }
  if (s.active_offers_without_hours > 0) {
    items.push({
      id: "offers-no-hours",
      tone: "warning",
      title: `${s.active_offers_without_hours} priced listing${s.active_offers_without_hours === 1 ? "" : "s"} with no opening hours`,
      detail: "The listing is live but no time can be booked.",
    });
  }
  if (s.businesses_without_sellable_listing > 0) {
    items.push({
      id: "biz-nothing-bookable",
      tone: "warning",
      title: `${s.businesses_without_sellable_listing} active business${s.businesses_without_sellable_listing === 1 ? " has" : "es have"} nothing bookable`,
      detail: "Approved, but no priced listing with opening hours, so players cannot book them yet.",
      href: "/app/businesses",
    });
  }
  if (s.resources_without_offer > 0) {
    items.push({
      id: "courts-no-offer",
      tone: "info",
      title: `${s.resources_without_offer} court${s.resources_without_offer === 1 ? "" : "s"} with no active listing`,
      detail: "The court exists but nothing is on sale for it.",
      href: "/app/businesses/resources",
    });
  }
  const pendingSetup = s.resources_by_status.pending_setup ?? 0;
  if (pendingSetup > 0 && s.sellable_offers > 0) {
    items.push({
      id: "courts-pending-setup",
      tone: "info",
      title: `${pendingSetup} court${pendingSetup === 1 ? " is" : "s are"} still marked "pending setup"`,
      detail:
        "Listings on them are live and can be booked, but the court's own status was never advanced. HUDDL Play never changes it; staff can, with Make available on the court's page.",
      href: "/app/businesses/resources",
    });
  }

  for (const b of m.top_businesses) {
    if (b.bookings >= MIN_BOOKINGS_FOR_RATE && (b.cancellation_rate ?? 0) >= HIGH_CANCELLATION_PERCENT) {
      items.push({
        id: `cancel-${b.business_id}`,
        tone: "danger",
        title: `${b.name}: ${Math.round(b.cancellation_rate ?? 0)}% of bookings cancelled`,
        detail: `${b.cancelled} of ${b.bookings} bookings in this range were cancelled.`,
        href: `/app/businesses/${b.business_id}`,
        signal: { signal_type: "reliability", target_type: "business", target_id: b.business_id },
      });
    }
  }

  const conv = m.holds.conversion_rate;
  const decided = m.holds.converted + m.holds.expired + m.holds.cancelled;
  if (conv !== null && decided >= 10 && conv < LOW_CHECKOUT_CONVERSION_PERCENT) {
    items.push({
      id: "checkout-conversion",
      tone: "warning",
      title: `Only ${conv.toFixed(0)}% of checkouts complete`,
      detail: `${m.holds.expired} holds expired unpaid and ${m.holds.cancelled} were cancelled out of ${decided} that finished.`,
    });
  }

  if (m.cancellation_lead.after_start > 0) {
    items.push({
      id: "cancel-after-start",
      tone: "info",
      title: `${m.cancellation_lead.after_start} booking${m.cancellation_lead.after_start === 1 ? " was" : "s were"} cancelled after the start time`,
      detail: "Cancelled after the slot had begun. Worth checking whether these were no-shows recorded as cancellations.",
    });
  }
  if (m.cancelled_by.system_or_unattributed > 0) {
    items.push({
      id: "cancel-unattributed",
      tone: "info",
      title: `${m.cancelled_by.system_or_unattributed} cancellation${m.cancelled_by.system_or_unattributed === 1 ? "" : "s"} with no recorded person`,
      detail: "Cancelled by the system or with no actor on record, so who cancelled cannot be told.",
    });
  }

  // Interest with no supply. Individually when there are a few; as one line
  // when there are many, so a long tail of one-member sports is not noise.
  const gaps = m.sports.filter((sp) => sp.members_interested > 0 && sp.active_offers === 0);
  if (gaps.length > MAX_SPORT_GAP_ROWS) {
    const members = gaps.reduce((n, sp) => n + sp.members_interested, 0);
    items.push({
      id: "sport-gaps",
      tone: "info",
      title: `${gaps.length} sports members play have no business listing them`,
      detail: `${gaps
        .slice(0, 12)
        .map((sp) => `${sp.sport} (${sp.members_interested})`)
        .join(", ")}${gaps.length > 12 ? ", …" : ""}. ${members} member interests in total. See the table below.`,
    });
  }
  for (const sp of gaps.length > MAX_SPORT_GAP_ROWS ? [] : gaps) {
    items.push({
      id: `sport-gap-${sp.sport}`,
      tone: "info",
      title: `${sp.members_interested} member${sp.members_interested === 1 ? " plays" : "s play"} ${sp.sport}, but no business lists it`,
      detail: "Interest with nothing to book.",
      ...(sp.sport_id
        ? { signal: { signal_type: "demand_gap" as const, target_type: "sport" as const, target_id: sp.sport_id } }
        : {}),
    });
  }

  for (const r of m.utilisation.resources) {
    if (r.has_hours && r.forward.available_hours > 0 && r.forward.booked_hours === 0) {
      items.push({
        id: `idle-${r.resource_id}`,
        tone: "info",
        title: `${r.resource} has nothing booked in the next ${m.utilisation.forward_days} days`,
        detail: `${Math.round(r.forward.available_hours)} open hours at ${r.business}, none taken yet.`,
        href: `/app/businesses/resources/${r.resource_id}`,
        signal: { signal_type: "utilization", target_type: "bookable_resource", target_id: r.resource_id },
      });
    }
  }

  const order: Record<AttentionTone, number> = { danger: 0, warning: 1, info: 2 };
  return items.sort((a, b) => order[a.tone] - order[b.tone]);
}

/** What the marketplace cannot measure yet, and what would make it measurable. */
export const NOT_MEASURABLE_YET: { title: string; why: string }[] = [
  {
    title: "No-show rate",
    why: "A booking has no attended or no-show status. Add a check-in or a business-marked outcome and this becomes a rate per court and per customer.",
  },
  {
    title: "Search to booking conversion",
    why: "HUDDL Play does not record searches or listing views, so only the step from checkout hold to booking can be measured, not the steps before it.",
  },
  {
    title: "Response time of businesses",
    why: "Measured only for bookings a business had to confirm (request to book). Business replies to players in chat and to support are not timed.",
  },
  {
    title: "Ratings and repeat-play quality",
    why: "There are no court reviews or ratings, so quality can only be inferred from cancellations and repeat bookings.",
  },
  {
    title: "Refund and dispute rates",
    why: "These need payments to flow through Xendit into the ledger. Until the first payment is recorded, there is nothing to divide by.",
  },
  {
    title: "Waitlists and unmet demand by time slot",
    why: "When a slot is full the player just sees it full. Recording the attempt would show which hours are under-supplied.",
  },
  {
    title: "Pricing against demand",
    why: "Needs enough bookings at different price points to compare. Shown per sport in the supply and demand table once there is volume.",
  },
];
